import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import MaterialIcon from '../../elements/MaterialIcon.jsx';
import MonthPicker from './MonthPicker.jsx';
import DateHelper from '../../../utilities/DateHelper';
import { describeSelection, steppedMonth } from '../../../utilities/monthSelection.js';
import { selectMonthRange, updateSelectedDate } from '../../../redux/actions/dashboard.js';

/**
 * The bar over the content. It names the section, and on a section that reads the selected month it
 * holds the month control: arrows that step a month, and the month itself, which opens the picker.
 * `trailing` is drawn at the right-hand end.
 */
export default function AppHeader({ title, showMonth, trailing = null }) {
	const dispatch = useDispatch();
	const selectedDates = useSelector((state) => state.dashboard.selectedDates);
	const budgets = useSelector((state) => state.dashboard.budgets);
	const [pickerOpen, setPickerOpen] = useState(false);
	const controlRef = useRef(null);

	const monthsWithData = useMemo(() => {
		const keys = new Set();
		(budgets || []).forEach((budget) => {
			if(budget.date) {
				keys.add(DateHelper.yearMonthSerialize(budget.date));
			}
		});
		return keys;
	}, [budgets]);

	const step = useCallback((direction) => {
		const month = steppedMonth(selectedDates, direction);
		if(month) {
			dispatch(updateSelectedDate(month));
		}
	}, [dispatch, selectedDates]);

	const closePicker = useCallback(() => setPickerOpen(false), []);
	const selectMonth = useCallback((month) => dispatch(updateSelectedDate(month)), [dispatch]);
	const selectRange = useCallback((from, to) => dispatch(selectMonthRange(from, to)), [dispatch]);

	return (
		<header className={`app-header ${showMonth ? 'has-month' : ''} ${pickerOpen ? 'is-picking' : ''}`}>
			<h1 className='app-header__title'>{title}</h1>
			{showMonth &&
			<div className='month-control' ref={controlRef}>
				<button type='button' className='shell-control month-control__step' aria-label='Previous month' onClick={() => step(-1)}>
					<MaterialIcon>chevron_left</MaterialIcon>
				</button>
				<button
					type='button'
					className='shell-control month-control__label'
					aria-haspopup='dialog'
					aria-expanded={pickerOpen}
					onClick={() => setPickerOpen((open) => !open)}
				>
					{describeSelection(selectedDates)}
					<MaterialIcon>arrow_drop_down</MaterialIcon>
				</button>
				<button type='button' className='shell-control month-control__step' aria-label='Next month' onClick={() => step(1)}>
					<MaterialIcon>chevron_right</MaterialIcon>
				</button>
				{pickerOpen &&
				<MonthPicker
					selectedDates={selectedDates}
					today={moment()}
					monthsWithData={monthsWithData}
					onSelect={selectMonth}
					onSelectRange={selectRange}
					onClose={closePicker}
					boundaryRef={controlRef}
				/>
				}
			</div>
			}
			{trailing &&
			<div className='app-header__trailing'>{trailing}</div>
			}
		</header>
	);
}
